import { AutoCompleteResolver } from '@ui/AutoCompleteField/types';
import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import {
  IndividualDetailsPost,
  IndividualImportPayload,
  IndividualImportResult,
  IndividualInterface,
  PostEmail
} from '@interfaces/Individual.interface';
import type { BulkImport } from '@/interfaces/BulkImport.interface';
import { PolicyStatistics } from '@interfaces/Policy.interface';
import type ApiClient from '../ApiClient';
import {
  CustomFieldInterface,
  CustomFieldValuePayload
} from '@/interfaces/CustomFields.interface';

export class ApiIndividual extends BaseApiClient {
  private api!: ApiClient;

  setApiClient(api: ApiClient) {
    this.api = api;
  }

  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<IndividualInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name_and_email = searchTerm;
      }
      // Remove type filter if it's "all" since backend doesn't support type=all
      if (listData.filters?.type === 'all') {
        const { type, ...restFilters } = listData.filters;
        listData.filters = restFilters;
      }
      this.client
        .getList('/individual', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async get(id: number): Promise<IndividualInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/individual/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async listDuplicates(id: number): Promise<IndividualInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/individual/${id}/duplicates`)
        .then((res) => {
          if (res.data?.data) {
            resolve(res.data.data);
          } else if (Array.isArray(res.data)) {
            resolve(res.data);
          } else {
            resolve([]);
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getIndividualPolicies(id: number): Promise<PolicyStatistics> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`policies/stats/individual/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async store(dto: IndividualDetailsPost): Promise<IndividualInterface> {
    return new Promise((resolve, reject) => {
      const { custom_fields: customFields, ...individualFields } = dto;
      const formData = this.buildFormData(individualFields);
      this.appendCustomFields(formData, customFields);
      this.client
        .post('/individual', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async update(
    individualId: number | undefined,
    dto: IndividualDetailsPost
  ): Promise<IndividualInterface> {
    const { custom_fields: customFields, ...individualFields } = dto;
    const formData = this.buildFormData(individualFields);
    this.appendCustomFields(formData, customFields);
    formData.append('_method', 'PATCH');

    return new Promise((resolve, reject) => {
      this.client
        .post(`/individual/${individualId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateCustomFields(
    individualId: number,
    payload: { custom_fields: CustomFieldValuePayload[] }
  ): Promise<IndividualInterface> {
    const response = await this.client.patch(
      `/individual/${individualId}`,
      payload
    );

    if (!response.data) {
      throw new Error('Invalid response');
    }

    return response.data;
  }

  private buildFormData(dto: Omit<IndividualDetailsPost, 'custom_fields'>) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(dto)) {
      if (value === null || value === undefined) {
        continue;
      }
      if (key === 'file' && (value === '' || !(value instanceof Blob))) {
        continue;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(`${key}[]`, String(item));
        });
        continue;
      }
      if (value instanceof File) {
        formData.append(key, value, value.name);
        continue;
      }
      formData.append(key, value as string | Blob);
    }
    return formData;
  }

  private appendCustomFields(
    formData: FormData,
    customFields: CustomFieldValuePayload[] | undefined
  ): void {
    customFields?.forEach((customField, index) => {
      formData.append(
        `custom_fields[${index}][field_id]`,
        String(customField.field_id)
      );

      if (Array.isArray(customField.value)) {
        customField.value.forEach((selectedValue) => {
          formData.append(
            `custom_fields[${index}][value][]`,
            String(selectedValue)
          );
        });
        return;
      }

      formData.append(
        `custom_fields[${index}][value]`,
        customField.value === null ? '' : String(customField.value)
      );
    });
  }

  async sendEmail(dto: PostEmail) {
    return this.client.post('individual/email', dto);
  }

  autoCompleteResolver: AutoCompleteResolver = async (
    searchTerm,
    page,
    perPage
  ) => {
    const data = await this.list({ perPage, page }, searchTerm || undefined);
    return data.items.map((item) => ({
      value: item.id,
      label: item.full_name
    }));
  };

  async archive(ids: number[]) {
    return this.client.delete('/individual-bulk-delete', {
      data: { ids: ids.map((value: number) => value) }
    });
  }
  async invite(ids: number[]) {
    return this.client.post('/individual/portal-invite', {
      id: ids.map((value: number) => value)
    });
  }

  async getImportCustomFields(): Promise<
    (CustomFieldInterface & { read_only: boolean })[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/field-groups/individual/custom-fields', {
          headers: { Version: '2025-11-14' }
        })
        .then((res) => {
          if (res.data?.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async import(
    payload: IndividualImportPayload
  ): Promise<BulkImport<IndividualImportResult> | IndividualImportResult> {
    const formData = new FormData();

    formData.append('document', payload.document);
    formData.append('individual_type_id', String(payload.individual_type_id));
    if (payload.field_group_template_name) {
      formData.append('field_group_template_name', payload.field_group_template_name);
    }

    payload.fields.forEach((field, index) => {
      formData.append(`fields[${index}][label]`, field.label);

      if (field.field_type_id !== undefined) {
        formData.append(
          `fields[${index}][field_type_id]`,
          String(field.field_type_id)
        );
      }

      if (field.field_attribute_id !== undefined) {
        formData.append(
          `fields[${index}][field_attribute_id]`,
          String(field.field_attribute_id)
        );
      }

      if (field.order !== undefined) {
        formData.append(`fields[${index}][order]`, String(field.order));
      }
    });

    payload.do_not_import_columns?.forEach((column, index) => {
      formData.append(`do_not_import_columns[${index}]`, column);
    });

    payload.skip_rows?.forEach((row, index) => {
      formData.append(`skip_rows[${index}]`, String(row));
    });

    if (payload.validate_only !== undefined) {
      formData.append('validate_only', String(payload.validate_only));
    }

    return new Promise((resolve, reject) => {
      this.client
        .post('/individual/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { individualQueryKeys } from './query-keys';
export type { IndividualListQueryParams } from './query-keys';
export {
  useIndividualListQuery,
  useIndividualQuery,
  useIndividualDuplicatesQuery,
  useIndividualPoliciesQuery
} from './use-individual-queries';
export {
  useIndividualArchiveMutation,
  useIndividualInviteMutation,
  useIndividualStoreMutation,
  useIndividualUpdateMutation,
  useIndividualSendEmailMutation
} from './use-individual-mutations';
export type { IndividualUpdateVariables } from './use-individual-mutations';
